/* Shared building blocks for the Website admin (cards, buttons, modals, charts …) */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { absUrl, prepareImage, useWebApi } from "./api";

/* ================= helpers ================= */

export const num = (n) => Number(n || 0).toLocaleString("en-IN");
export const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
export const fdate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
export const fdt = (s) => (s ? new Date(s).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
export const ago = (s) => {
  if (!s) return "—";
  const m = Math.round((Date.now() - +new Date(s)) / 60000);
  if (m < 1) return "अभी";
  if (m < 60) return m + " min पहले";
  if (m < 1440) return Math.round(m / 60) + " घंटे पहले";
  if (m < 43200) return Math.round(m / 1440) + " दिन पहले";
  return fdate(s);
};
export const strip = (html) => String(html || "").replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
export const cx = (...a) => a.filter(Boolean).join(" ");

export function exportCsv(rows, name) {
  if (!rows || !rows.length) return;
  const cols = Object.keys(rows[0]);
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const csv = "﻿" + [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

export function useDebounced(value, ms = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/* ================= styles ================= */

export const WA_CSS = `
.wa-root{--wa-accent:#06b6d4;--wa-ink:#0f172a;--wa-muted:#64748b;--wa-line:#e2e8f0;--wa-bg:#f4f6fb;--wa-card:#ffffff;}
.dark .wa-root{--wa-ink:#e2e8f0;--wa-muted:#94a3b8;--wa-line:#2b3445;--wa-bg:#0d1321;--wa-card:#151c2c;}
.wa-root{background:var(--wa-bg);color:var(--wa-ink)}
.wa-card{background:var(--wa-card);border:1px solid var(--wa-line);border-radius:18px;box-shadow:0 1px 2px rgba(15,23,42,.04),0 8px 24px -18px rgba(15,23,42,.18)}
.wa-muted{color:var(--wa-muted)}
.wa-line{border-color:var(--wa-line)}
.wa-input{width:100%;height:40px;border-radius:12px;border:1px solid var(--wa-line);background:var(--wa-card);padding:0 12px;font-size:14px;color:var(--wa-ink);outline:none;transition:border-color .15s,box-shadow .15s}
.wa-input:focus{border-color:var(--wa-accent);box-shadow:0 0 0 3px color-mix(in srgb,var(--wa-accent) 20%,transparent)}
textarea.wa-input{height:auto;padding:10px 12px;line-height:1.55;resize:vertical}
select.wa-input{padding-right:28px}
.wa-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;height:38px;padding:0 14px;border-radius:12px;font-size:13.5px;font-weight:600;white-space:nowrap;transition:transform .12s,box-shadow .2s,background-color .15s,opacity .15s;border:1px solid transparent;cursor:pointer}
.wa-btn:active{transform:scale(.97)}
.wa-btn:disabled{opacity:.5;cursor:not-allowed;transform:none}
.wa-btn-sm{height:32px;padding:0 10px;font-size:12.5px;border-radius:10px}
.wa-btn-primary{background:var(--wa-accent);color:#fff;box-shadow:0 8px 18px -10px var(--wa-accent)}
.wa-btn-primary:hover{filter:brightness(1.06);box-shadow:0 10px 22px -10px var(--wa-accent)}
.wa-btn-ghost{background:transparent;border-color:var(--wa-line);color:var(--wa-ink)}
.wa-btn-ghost:hover{background:color-mix(in srgb,var(--wa-ink) 5%,transparent)}
.wa-btn-soft{background:color-mix(in srgb,var(--wa-accent) 12%,transparent);color:var(--wa-accent)}
.wa-btn-soft:hover{background:color-mix(in srgb,var(--wa-accent) 18%,transparent)}
.wa-btn-danger{background:#ef4444;color:#fff}
.wa-btn-danger:hover{background:#dc2626}
.wa-btn-success{background:#16a34a;color:#fff}
.wa-btn-success:hover{background:#15803d}
.wa-btn-warn{background:#f59e0b;color:#fff}
.wa-tbl{width:100%;border-collapse:separate;border-spacing:0;font-size:13.5px}
.wa-tbl th{position:sticky;top:0;background:var(--wa-card);text-align:left;font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--wa-muted);padding:10px 12px;border-bottom:1px solid var(--wa-line);z-index:1}
.wa-tbl td{padding:11px 12px;border-bottom:1px solid var(--wa-line);vertical-align:middle}
.wa-tbl tbody tr{transition:background-color .12s}
.wa-tbl tbody tr:hover{background:color-mix(in srgb,var(--wa-accent) 5%,transparent)}
.wa-tbl tbody tr.wa-sel{background:color-mix(in srgb,var(--wa-accent) 9%,transparent)}
.wa-tbl tr:last-child td{border-bottom:0}
@keyframes waIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
.wa-in{animation:waIn .35s cubic-bezier(.2,.7,.2,1) both}
@keyframes waFade{from{opacity:0}to{opacity:1}}
@keyframes waPop{from{opacity:0;transform:translateY(10px) scale(.98)}to{opacity:1;transform:none}}
@keyframes waSlide{from{transform:translateX(100%)}to{transform:none}}
.wa-overlay{animation:waFade .18s ease both}
.wa-modal{animation:waPop .22s cubic-bezier(.2,.8,.2,1) both}
.wa-drawer{animation:waSlide .26s cubic-bezier(.2,.8,.2,1) both}
@keyframes waToast{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}
.wa-toast{animation:waToast .25s ease both}
@keyframes waShimmer{0%{background-position:-400px 0}100%{background-position:400px 0}}
.wa-skel{background:linear-gradient(90deg,color-mix(in srgb,var(--wa-ink) 6%,transparent) 0,color-mix(in srgb,var(--wa-ink) 11%,transparent) 50%,color-mix(in srgb,var(--wa-ink) 6%,transparent) 100%);background-size:800px 100%;animation:waShimmer 1.3s linear infinite;border-radius:10px}
.wa-scroll{scrollbar-width:thin}
.wa-switch{position:relative;width:42px;height:24px;border-radius:999px;background:#cbd5e1;transition:background .2s;flex-shrink:0;cursor:pointer;border:0}
.wa-switch::after{content:"";position:absolute;top:3px;left:3px;width:18px;height:18px;border-radius:50%;background:#fff;box-shadow:0 1px 3px rgba(0,0,0,.25);transition:transform .2s}
.wa-switch.on{background:var(--wa-accent)}
.wa-switch.on::after{transform:translateX(18px)}
.dark .wa-switch{background:#334155}
.dark .wa-switch.on{background:var(--wa-accent)}
.wa-editor{min-height:420px;outline:none;padding:18px 20px;font-size:16px;line-height:1.8}
.wa-editor h2{font-size:1.5em;font-weight:800;margin:1em 0 .5em}
.wa-editor h3{font-size:1.25em;font-weight:700;margin:1em 0 .4em}
.wa-editor h4{font-size:1.1em;font-weight:700;margin:.9em 0 .3em}
.wa-editor p{margin:.6em 0}
.wa-editor ul{list-style:disc;padding-left:1.4em}
.wa-editor ol{list-style:decimal;padding-left:1.4em}
.wa-editor blockquote{border-left:3px solid var(--wa-accent);padding:.4em 1em;margin:1em 0;background:color-mix(in srgb,var(--wa-accent) 6%,transparent);border-radius:0 10px 10px 0}
.wa-editor a{color:var(--wa-accent);text-decoration:underline}
.wa-editor img{max-width:100%;height:auto;border-radius:12px;margin:.8em 0}
.wa-editor table{border-collapse:collapse;width:100%}
.wa-editor td,.wa-editor th{border:1px solid var(--wa-line);padding:6px 10px}
.wa-editor:empty:before{content:attr(data-ph);color:var(--wa-muted)}
.wa-tip{pointer-events:none;position:absolute;z-index:5;transform:translate(-50%,-100%);background:#0f172a;color:#fff;font-size:12px;border-radius:8px;padding:6px 9px;white-space:nowrap;box-shadow:0 8px 20px -8px rgba(0,0,0,.4)}
@media (prefers-reduced-motion: reduce){.wa-in,.wa-modal,.wa-drawer,.wa-overlay,.wa-toast,.wa-skel{animation:none}.wa-btn:active{transform:none}}
`;

/* ================= app context: api + toasts + confirm ================= */

const Ctx = createContext(null);
export const useWA = () => useContext(Ctx);

export function WAProvider({ children }) {
  const api = useWebApi();
  const [toasts, setToasts] = useState([]);
  const [dialog, setDialog] = useState(null);

  const toast = useCallback((msg, tone = "ok") => {
    const id = Math.random().toString(36).slice(2);
    setToasts((t) => [...t, { id, msg, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), tone === "error" ? 6000 : 3200);
  }, []);

  const confirm = useCallback(
    (opts) =>
      new Promise((resolve) => {
        setDialog({ ...(typeof opts === "string" ? { title: opts } : opts), kind: "confirm", resolve });
      }),
    []
  );
  const prompt = useCallback(
    (opts) =>
      new Promise((resolve) => {
        setDialog({ ...(typeof opts === "string" ? { title: opts } : opts), kind: "prompt", resolve });
      }),
    []
  );

  // run an action with automatic error toast
  const run = useCallback(
    async (action, params, okMsg) => {
      try {
        const r = await api(action, params);
        if (okMsg) toast(okMsg);
        return r;
      } catch (e) {
        toast(e.message, "error");
        throw e;
      }
    },
    [api, toast]
  );

  const value = useMemo(() => ({ api, run, toast, confirm, prompt }), [api, run, toast, confirm, prompt]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="fixed bottom-5 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2" style={{ zIndex: 3000 }}>
        {toasts.map((t) => (
          <div key={t.id} role="status" className={cx("wa-toast px-4 py-3 rounded-xl text-sm font-semibold shadow-2xl max-w-[90vw] flex items-center gap-2", t.tone === "error" ? "bg-rose-600 text-white" : "bg-slate-900 text-white")}>
            <i className={t.tone === "error" ? "fa-solid fa-circle-exclamation" : "fa-solid fa-circle-check text-emerald-400"} />
            {t.msg}
          </div>
        ))}
      </div>
      {dialog && <Dialog d={dialog} close={(v) => { dialog.resolve(v); setDialog(null); }} />}
    </Ctx.Provider>
  );
}

function Dialog({ d, close }) {
  const [val, setVal] = useState(d.defaultValue || "");
  const isPrompt = d.kind === "prompt";
  return (
    <Modal open onClose={() => close(isPrompt ? null : false)} title={d.title} width={440}
      footer={
        <>
          <Btn variant="ghost" onClick={() => close(isPrompt ? null : false)}>रद्द करें</Btn>
          <Btn variant={d.danger ? "danger" : "primary"} onClick={() => close(isPrompt ? val : true)} autoFocus={!isPrompt}>
            {d.ok || (d.danger ? "हाँ, हटाएँ" : "ठीक है")}
          </Btn>
        </>
      }>
      {d.text && <p className="text-sm wa-muted leading-relaxed">{d.text}</p>}
      {isPrompt && (
        d.multiline ? (
          <textarea autoFocus rows={4} className="wa-input mt-3" value={val} placeholder={d.placeholder} onChange={(e) => setVal(e.target.value)} />
        ) : (
          <input autoFocus className="wa-input mt-3" value={val} placeholder={d.placeholder} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && close(val)} />
        )
      )}
    </Modal>
  );
}

/* ================= layout pieces ================= */

export function PageHead({ icon, title, subtitle, actions, color = "var(--wa-accent)" }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6 wa-in">
      <div className="flex items-center gap-3.5 min-w-0">
        {icon && (
          <span className="w-12 h-12 rounded-2xl grid place-items-center text-lg text-white shrink-0" style={{ background: `linear-gradient(135deg, ${color}, #6366f1)`, boxShadow: `0 12px 24px -12px ${color}` }}>
            <i className={icon} />
          </span>
        )}
        <div className="min-w-0">
          <h1 className="text-[22px] md:text-2xl font-extrabold tracking-tight leading-tight">{title}</h1>
          {subtitle && <p className="text-sm wa-muted mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Card({ title, subtitle, actions, children, className, pad = true, icon }) {
  return (
    <section className={cx("wa-card wa-in", className)}>
      {(title || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-3 px-5 pt-4 pb-3">
          <div className="flex items-center gap-2.5 min-w-0">
            {icon && <i className={cx(icon, "text-[15px]")} style={{ color: "var(--wa-accent)" }} />}
            <div className="min-w-0">
              {title && <h2 className="font-bold text-[15px] leading-tight">{title}</h2>}
              {subtitle && <p className="text-xs wa-muted mt-0.5">{subtitle}</p>}
            </div>
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={pad ? "px-5 pb-5" + (title ? "" : " pt-5") : ""}>{children}</div>
    </section>
  );
}

export function Btn({ variant = "ghost", size, icon, loading, children, className, ...rest }) {
  return (
    <button type="button" className={cx("wa-btn", `wa-btn-${variant}`, size === "sm" && "wa-btn-sm", className)} disabled={loading || rest.disabled} {...rest}>
      {loading ? <i className="fa-solid fa-spinner fa-spin" /> : icon ? <i className={icon} /> : null}
      {children}
    </button>
  );
}

const TONES = {
  gray: "bg-slate-100 text-slate-600 dark:bg-slate-700/50 dark:text-slate-300",
  green: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  amber: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  red: "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
  blue: "bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300",
  violet: "bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300",
  cyan: "bg-cyan-50 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300",
  pink: "bg-pink-50 text-pink-700 dark:bg-pink-500/15 dark:text-pink-300",
};
export function Badge({ tone = "gray", children, icon, className }) {
  return (
    <span className={cx("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap", TONES[tone] || TONES.gray, className)}>
      {icon && <i className={icon} />}
      {children}
    </span>
  );
}

export const STATUS_TONE = { PUBLISHED: "green", DRAFT: "gray", SCHEDULED: "blue", PRIVATE: "violet", UNLISTED: "amber", PENDING: "amber", APPROVED: "green", REJECTED: "red", CANCELLED: "gray", NEW: "cyan", SEEN: "gray", REPLIED: "green", CLOSED: "gray", REQUESTED: "amber", NONE: "gray" };
export const StatusBadge = ({ s }) => <Badge tone={STATUS_TONE[s] || "gray"}>{s === "NONE" ? "—" : s}</Badge>;

export function Field({ label, hint, children, className, right }) {
  return (
    <label className={cx("block", className)}>
      {label && (
        <span className="flex items-center justify-between mb-1.5">
          <span className="text-[12.5px] font-semibold">{label}</span>
          {right && <span className="text-[11px] wa-muted">{right}</span>}
        </span>
      )}
      {children}
      {hint && <span className="block text-[11.5px] wa-muted mt-1 leading-snug">{hint}</span>}
    </label>
  );
}

export function Toggle({ checked, onChange, label, desc, disabled }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold">{label}</p>
        {desc && <p className="text-xs wa-muted mt-0.5 leading-snug">{desc}</p>}
      </div>
      <button type="button" role="switch" aria-checked={!!checked} aria-label={label} disabled={disabled} onClick={() => onChange(!checked)} className={cx("wa-switch", checked && "on")} />
    </div>
  );
}

export function Tabs({ tabs, value, onChange }) {
  return (
    <div className="flex gap-1 p-1 rounded-2xl overflow-x-auto wa-scroll" style={{ background: "color-mix(in srgb, var(--wa-ink) 5%, transparent)" }} role="tablist">
      {tabs.map((t) => {
        const [k, l, n] = Array.isArray(t) ? t : [t.k, t.l, t.n];
        const on = value === k;
        return (
          <button key={k} type="button" role="tab" aria-selected={on} onClick={() => onChange(k)}
            className={cx("px-3.5 h-9 rounded-xl text-[13px] font-semibold whitespace-nowrap transition-all flex items-center gap-2", on ? "shadow-sm" : "wa-muted hover:opacity-80")}
            style={on ? { background: "var(--wa-card)", color: "var(--wa-ink)" } : {}}>
            {l}
            {n !== undefined && n !== null && <span className={cx("text-[10.5px] px-1.5 rounded-full font-bold", on ? "text-white" : "")} style={{ background: on ? "var(--wa-accent)" : "color-mix(in srgb, var(--wa-ink) 9%, transparent)" }}>{n}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function Search({ value, onChange, placeholder = "खोजें…", className }) {
  return (
    <div className={cx("relative", className)}>
      <i className="fa-solid fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-[12px] wa-muted pointer-events-none" />
      <input className="wa-input" style={{ paddingLeft: 34 }} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} aria-label={placeholder} />
      {value && (
        <button type="button" onClick={() => onChange("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md wa-muted hover:opacity-70" aria-label="Clear">
          <i className="fa-solid fa-xmark text-[12px]" />
        </button>
      )}
    </div>
  );
}

export function Modal({ open, onClose, title, children, footer, width = 560 }) {
  useEffect(() => {
    if (!open) return undefined;
    const k = (e) => e.key === "Escape" && onClose && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="wa-overlay fixed inset-0 flex items-end sm:items-center justify-center p-0 sm:p-6" style={{ zIndex: 2500, background: "rgba(15,23,42,.55)", backdropFilter: "blur(3px)" }} onMouseDown={(e) => e.target === e.currentTarget && onClose && onClose()}>
      <div className="wa-modal wa-card w-full max-h-[92vh] flex flex-col rounded-b-none sm:rounded-[18px]" style={{ maxWidth: width }} role="dialog" aria-modal="true" aria-label={typeof title === "string" ? title : undefined}>
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b wa-line">
          <h3 className="font-bold text-base">{title}</h3>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg wa-muted hover:opacity-70" aria-label="Close"><i className="fa-solid fa-xmark" /></button>
        </div>
        <div className="p-5 overflow-y-auto wa-scroll">{children}</div>
        {footer && <div className="flex flex-wrap justify-end gap-2 px-5 py-3.5 border-t wa-line">{footer}</div>}
      </div>
    </div>
  );
}

export function Drawer({ open, onClose, title, subtitle, children, footer, width = 620 }) {
  useEffect(() => {
    if (!open) return undefined;
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="wa-overlay fixed inset-0 flex justify-end" style={{ zIndex: 2400, background: "rgba(15,23,42,.45)" }} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="wa-drawer h-full w-full flex flex-col shadow-2xl" style={{ maxWidth: width, background: "var(--wa-bg)" }} role="dialog" aria-modal="true">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b wa-line" style={{ background: "var(--wa-card)" }}>
          <div className="min-w-0">
            <h3 className="font-bold text-lg leading-tight truncate">{title}</h3>
            {subtitle && <div className="text-xs wa-muted mt-1">{subtitle}</div>}
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-xl wa-muted hover:opacity-70 shrink-0" aria-label="Close"><i className="fa-solid fa-xmark text-lg" /></button>
        </div>
        <div className="flex-1 overflow-y-auto wa-scroll p-5">{children}</div>
        {footer && <div className="flex flex-wrap justify-end gap-2 px-5 py-3.5 border-t wa-line" style={{ background: "var(--wa-card)" }}>{footer}</div>}
      </aside>
    </div>
  );
}

export function Empty({ icon = "fa-regular fa-folder-open", title = "कुछ नहीं मिला", text, action }) {
  return (
    <div className="text-center py-14 px-4">
      <span className="inline-grid place-items-center w-14 h-14 rounded-2xl text-xl mb-3" style={{ background: "color-mix(in srgb, var(--wa-accent) 12%, transparent)", color: "var(--wa-accent)" }}>
        <i className={icon} />
      </span>
      <p className="font-bold">{title}</p>
      {text && <p className="text-sm wa-muted mt-1 max-w-sm mx-auto">{text}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Skeleton({ rows = 6, h = 44 }) {
  return (
    <div className="space-y-2.5 p-1">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="wa-skel" style={{ height: h, opacity: 1 - i * 0.1 }} />
      ))}
    </div>
  );
}

export function ErrorBox({ msg, retry }) {
  if (!msg) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-rose-200 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/10 px-4 py-3 text-sm text-rose-700 dark:text-rose-300 mb-4">
      <i className="fa-solid fa-triangle-exclamation" />
      <span className="flex-1 min-w-[200px]">{msg}</span>
      {retry && <Btn size="sm" variant="ghost" icon="fa-solid fa-rotate" onClick={retry}>फिर से</Btn>}
    </div>
  );
}

export function Pager({ total, take, skip, onChange }) {
  if (!total || total <= take) return null;
  const page = Math.floor(skip / take) + 1;
  const pages = Math.ceil(total / take);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t wa-line text-sm">
      <span className="wa-muted">{num(skip + 1)}–{num(Math.min(total, skip + take))} / {num(total)}</span>
      <div className="flex items-center gap-1.5">
        <Btn size="sm" icon="fa-solid fa-chevron-left" disabled={page <= 1} onClick={() => onChange(Math.max(0, skip - take))} aria-label="Previous" />
        <span className="px-2 font-semibold tabular-nums">{page} / {pages}</span>
        <Btn size="sm" icon="fa-solid fa-chevron-right" disabled={page >= pages} onClick={() => onChange(skip + take)} aria-label="Next" />
      </div>
    </div>
  );
}

export function Avatar({ name, image, size = 36 }) {
  const [bad, setBad] = useState(false);
  const ini = String(name || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";
  const hue = [...String(name || "x")].reduce((s, c) => s + c.charCodeAt(0), 0) % 360;
  if (image && !bad) return <img src={absUrl(image)} alt="" onError={() => setBad(true)} className="rounded-full object-cover shrink-0" style={{ width: size, height: size }} />;
  return (
    <span className="rounded-full grid place-items-center text-white font-bold shrink-0" style={{ width: size, height: size, fontSize: size * 0.36, background: `linear-gradient(135deg, hsl(${hue} 70% 55%), hsl(${(hue + 40) % 360} 70% 45%))` }}>
      {ini}
    </span>
  );
}

export function Stat({ icon, label, value, hint, color = "#06b6d4", onClick, alert }) {
  const El = onClick ? "button" : "div";
  return (
    <El type={onClick ? "button" : undefined} onClick={onClick} className={cx("wa-card wa-in p-4 text-left w-full relative overflow-hidden", onClick && "transition-transform hover:-translate-y-0.5")}>
      <span className="absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-[.08]" style={{ background: color }} />
      <div className="flex items-start justify-between gap-3">
        <p className="text-[12.5px] font-semibold wa-muted">{label}</p>
        <span className="w-9 h-9 rounded-xl grid place-items-center text-[14px] shrink-0" style={{ background: color + "1f", color }}>
          <i className={icon} />
        </span>
      </div>
      <p className="text-[26px] font-extrabold tracking-tight mt-1 tabular-nums">{value}</p>
      <p className={cx("text-[11.5px] mt-0.5", alert ? "text-amber-600 dark:text-amber-400 font-semibold" : "wa-muted")}>{hint || "\u00a0"}</p>
    </El>
  );
}

/* ================= charts (single series, hover tooltip) ================= */

export function BarChart({ data, height = 200, format = num, color = "var(--wa-accent)", label = "" }) {
  const [hover, setHover] = useState(null);
  const ref = useRef(null);
  // a "nice" top value so the axis never shows repeated numbers (1, 1, 0)
  const raw = Math.max(0, ...data.map((d) => d.value));
  const max = raw <= 10 ? Math.max(2, Math.ceil(raw / 2) * 2) : raw;
  const W = 1000;
  const H = height;
  const pad = { t: 10, b: 24, l: 0, r: 0 };
  const bw = W / Math.max(1, data.length);
  const gap = Math.min(6, bw * 0.25);
  const ticks = [0, 0.5, 1].map((f) => Math.round(max * f));
  const every = Math.ceil(data.length / 8);
  return (
    <div className="relative" ref={ref}>
      <div className="flex gap-2">
        <div className="flex flex-col justify-between text-[10.5px] wa-muted tabular-nums text-right" style={{ height: H - pad.b, paddingTop: pad.t }}>
          {[...ticks].reverse().map((t, i) => <span key={i} className="leading-none">{format(t)}</span>)}
        </div>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="flex-1 min-w-0" style={{ height: H }} role="img" aria-label={label} onMouseLeave={() => setHover(null)}>
          {[0, 0.5, 1].map((f, i) => (
            <line key={i} x1="0" x2={W} y1={pad.t + (H - pad.t - pad.b) * (1 - f)} y2={pad.t + (H - pad.t - pad.b) * (1 - f)} stroke="var(--wa-line)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          ))}
          {data.map((d, i) => {
            const h = ((H - pad.t - pad.b) * d.value) / max;
            const x = i * bw + gap / 2;
            const y = H - pad.b - h;
            const r = Math.min(4, (bw - gap) / 2, h);
            return (
              <g key={i} onMouseEnter={() => setHover(i)}>
                <rect x={i * bw} y={0} width={bw} height={H - pad.b} fill="transparent" />
                {h > 0 && <path d={`M${x},${H - pad.b} V${y + r} Q${x},${y} ${x + r},${y} H${x + bw - gap - r} Q${x + bw - gap},${y} ${x + bw - gap},${y + r} V${H - pad.b} Z`} fill={color} opacity={hover === null || hover === i ? 1 : 0.45} />}
              </g>
            );
          })}
        </svg>
      </div>
      <div className="relative h-4 text-[10.5px] wa-muted mt-1" style={{ marginLeft: 30 }}>
        {data.map((d, i) =>
          i % every === 0 ? (
            <span key={i} className="absolute -translate-x-1/2 whitespace-nowrap" style={{ left: `${((i + 0.5) / data.length) * 100}%` }}>{d.short || d.label}</span>
          ) : null
        )}
      </div>
      {hover !== null && data[hover] && (
        <div className="wa-tip" style={{ left: `calc(30px + (100% - 30px) * ${(hover + 0.5) / data.length})`, top: 6 }}>
          <b>{format(data[hover].value)}</b> <span className="opacity-70">· {data[hover].label}</span>
        </div>
      )}
    </div>
  );
}

export function HBars({ rows, format = num, color = "var(--wa-accent)" }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  if (!rows.length) return <p className="text-sm wa-muted py-6 text-center">अभी कोई डेटा नहीं</p>;
  return (
    <ul className="space-y-3">
      {rows.map((r) => (
        <li key={r.label} title={`${r.label}: ${format(r.value)}`}>
          <div className="flex justify-between text-[13px] mb-1">
            <span className="font-medium truncate pr-3">{r.label}</span>
            <span className="tabular-nums font-semibold">{format(r.value)}</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: "color-mix(in srgb, var(--wa-ink) 7%, transparent)" }}>
            <div className="h-full rounded-full" style={{ width: `${(r.value / max) * 100}%`, background: color, transition: "width .6s cubic-bezier(.2,.7,.2,1)" }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

/* ================= image picker (uploads to the website) ================= */

export function ImageInput({ value, onChange, folder = "admin", label = "Image", hint, square }) {
  const { api, toast } = useWA();
  const file = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast("सिर्फ image चुनें", "error");
    setBusy(true);
    try {
      const data = await prepareImage(f);
      const r = await api("upload", { data, folder });
      onChange(r.url);
      toast("Image upload हो गई");
    } catch (e) {
      toast(e.message, "error");
    }
    setBusy(false);
  };
  return (
    <Field label={label} hint={hint}>
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => file.current && file.current.click()} className={cx("relative shrink-0 rounded-2xl overflow-hidden border-2 border-dashed wa-line grid place-items-center hover:opacity-80", square ? "w-24 h-24" : "w-36 h-24")} style={{ background: "color-mix(in srgb, var(--wa-ink) 4%, transparent)" }} aria-label="Upload image">
          {value ? <img src={absUrl(value)} alt="" className="w-full h-full object-cover" /> : <i className={busy ? "fa-solid fa-spinner fa-spin wa-muted" : "fa-regular fa-image text-xl wa-muted"} />}
          {busy && value && <span className="absolute inset-0 grid place-items-center bg-black/40 text-white"><i className="fa-solid fa-spinner fa-spin" /></span>}
        </button>
        <div className="flex-1 min-w-0 space-y-2">
          <input className="wa-input" value={value || ""} placeholder="https://… या upload करें" onChange={(e) => onChange(e.target.value)} />
          <div className="flex gap-2">
            <Btn size="sm" variant="soft" icon="fa-solid fa-upload" loading={busy} onClick={() => file.current && file.current.click()}>Upload</Btn>
            {value && <Btn size="sm" variant="ghost" icon="fa-regular fa-trash-can" onClick={() => onChange("")}>हटाएँ</Btn>}
          </div>
        </div>
      </div>
      <input ref={file} type="file" accept="image/*" className="hidden" onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} />
    </Field>
  );
}

/* ================= data loader hook ================= */

export function useLoad(action, params, deps) {
  const { api } = useWA();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const p = useRef(params);
  p.current = params;
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    api(action, p.current)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, tick, ...(deps || [])]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, loading, reload, setData };
}

/* ================= bulk select ================= */

export function useSelection(items) {
  const [sel, setSel] = useState([]);
  const idsKey = (items || []).map((x) => x.id).join(",");
  useEffect(() => setSel([]), [idsKey]);
  const all = items && items.length > 0 && sel.length === items.length;
  return {
    sel,
    setSel,
    has: (id) => sel.includes(id),
    toggle: (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id])),
    toggleAll: () => setSel(all ? [] : (items || []).map((x) => x.id)),
    all,
    clear: () => setSel([]),
  };
}

export function Check({ checked, onChange, label }) {
  return <input type="checkbox" aria-label={label || "Select"} checked={checked} onChange={onChange} className="w-4 h-4 rounded cursor-pointer" style={{ accentColor: "var(--wa-accent)" }} />;
}

export function BulkBar({ count, onClear, children }) {
  if (!count) return null;
  return (
    <div className="wa-in sticky top-2 flex flex-wrap items-center gap-2 rounded-2xl px-4 py-2.5 mb-3 text-white shadow-xl" style={{ zIndex: 20, background: "linear-gradient(90deg, #0f172a, #1e293b)" }}>
      <b className="text-sm mr-1">{count} चुने गए</b>
      <div className="flex flex-wrap gap-2 flex-1">{children}</div>
      <button type="button" onClick={onClear} className="text-xs opacity-70 hover:opacity-100 px-2">साफ़ करें</button>
    </div>
  );
}
